import React, { useEffect, useState } from "react";
import { Sun, CloudRain, Snowflake, Wind, Clock, Receipt } from "lucide-react";

/**
 * AmbientContextStrip — India + HVAC chrome (LIGHT theme).
 *
 * Live IST clock, Indian season indicator with tone-appropriate imagery,
 * financial-year quarter, and GSTR-3B filing deadline proximity.
 */
const SEASONS = {
  1:  { name: "Winter",        short: "WINTER",       icon: Snowflake, wash: "bg-gradient-to-br from-sky-50 to-white",       tint: "text-sky-700",     ring: "ring-sky-100" },
  2:  { name: "Late Winter",   short: "LATE WINTER",  icon: Snowflake, wash: "bg-gradient-to-br from-sky-50/70 to-white",    tint: "text-sky-700",     ring: "ring-sky-100" },
  3:  { name: "Pre-Summer",    short: "PRE-SUMMER",   icon: Sun,       wash: "bg-gradient-to-br from-amber-50 to-white",     tint: "text-amber-700",   ring: "ring-amber-100" },
  4:  { name: "Peak Summer",   short: "PEAK SUMMER",  icon: Sun,       wash: "bg-gradient-to-br from-orange-100 to-white",   tint: "text-orange-700",  ring: "ring-orange-100" },
  5:  { name: "Peak Summer",   short: "PEAK SUMMER",  icon: Sun,       wash: "bg-gradient-to-br from-orange-100 to-white",   tint: "text-orange-700",  ring: "ring-orange-100" },
  6:  { name: "Pre-Monsoon",   short: "PRE-MONSOON",  icon: Wind,      wash: "bg-gradient-to-br from-emerald-50 to-white",   tint: "text-emerald-700", ring: "ring-emerald-100" },
  7:  { name: "Monsoon",       short: "MONSOON",      icon: CloudRain, wash: "bg-gradient-to-br from-blue-50 to-white",      tint: "text-blue-700",    ring: "ring-blue-100" },
  8:  { name: "Monsoon",       short: "MONSOON",      icon: CloudRain, wash: "bg-gradient-to-br from-blue-50 to-white",      tint: "text-blue-700",    ring: "ring-blue-100" },
  9:  { name: "Late Monsoon",  short: "LATE MONSOON", icon: CloudRain, wash: "bg-gradient-to-br from-blue-50/70 to-white",   tint: "text-blue-700",    ring: "ring-blue-100" },
  10: { name: "Post-Monsoon",  short: "POST-MONSOON", icon: Wind,      wash: "bg-gradient-to-br from-teal-50 to-white",      tint: "text-teal-700",    ring: "ring-teal-100" },
  11: { name: "Cool & Dry",    short: "COOL & DRY",   icon: Wind,      wash: "bg-gradient-to-br from-cyan-50 to-white",      tint: "text-cyan-700",    ring: "ring-cyan-100" },
  12: { name: "Winter",        short: "WINTER",       icon: Snowflake, wash: "bg-gradient-to-br from-sky-50 to-white",       tint: "text-sky-700",     ring: "ring-sky-100" },
};

function seasonalDemandHint(month) {
  if ([3, 4, 5].includes(month)) return "HIGH · Peak AC install season";
  if ([6, 7, 8, 9].includes(month)) return "MED · Service / maintenance heavy";
  if ([10, 11].includes(month)) return "MED · Commissioning / retrofits";
  return "LOW · Winter — plan next FY intake";
}

function currentFY(now) {
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const start = m >= 4 ? y : y - 1;
  const end = start + 1;
  const q = quarterFor(m);
  return { label: `FY${String(start).slice(-2)}-${String(end).slice(-2)}`, quarter: q };
}

function quarterFor(m) {
  if (m >= 4 && m <= 6) return "Q1";
  if (m >= 7 && m <= 9) return "Q2";
  if (m >= 10 && m <= 12) return "Q3";
  return "Q4";
}

function gstUrgencyTone(days) {
  if (days <= 5) return "text-red-600";
  if (days <= 10) return "text-amber-700";
  return "text-emerald-700";
}

function gstr3bStatus(now) {
  const y = now.getFullYear();
  const m = now.getMonth();
  const due = new Date(y, m, 20);
  if (now > due) {
    const next = new Date(y, m + 1, 20);
    const daysLeft = Math.ceil((next - now) / 86_400_000);
    return { days: daysLeft, forMonth: monthName(m), label: `GSTR-3B · next in ${daysLeft}d` };
  }
  const daysLeft = Math.ceil((due - now) / 86_400_000);
  return { days: daysLeft, forMonth: monthName(m - 1), label: `GSTR-3B · file in ${daysLeft}d` };
}

function monthName(m) {
  const mm = ((m % 12) + 12) % 12;
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][mm];
}

export default function AmbientContextStrip() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeIST = new Intl.DateTimeFormat("en-IN", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false, timeZone: "Asia/Kolkata",
  }).format(now);
  const dateIST = new Intl.DateTimeFormat("en-IN", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(now);

  const month = now.getMonth() + 1;
  const s = SEASONS[month];
  const SIcon = s.icon;
  const demand = seasonalDemandHint(month);
  const fy = currentFY(now);
  const gst = gstr3bStatus(now);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-stone-200 ${s.wash} p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_10px_20px_-15px_rgba(234,88,12,0.15)]`}
      data-testid="ambient-context-strip"
    >
      {/* Season block */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-tc-mono tracking-[0.24em] text-stone-500">SEASON · INDIA</div>
          <div className={`flex items-center gap-2 mt-1 ${s.tint}`}>
            <SIcon size={20} strokeWidth={1.7} />
            <div className="font-tc-display text-[22px] font-black tracking-tight text-stone-900">{s.name}</div>
          </div>
          <div className="text-[11px] text-stone-500 mt-1 font-tc-mono">{demand}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] font-tc-mono tracking-[0.24em] text-stone-500">IST · LIVE</div>
          <div className="font-tc-mono text-[22px] font-bold text-stone-900 mt-1" data-testid="ambient-ist-time">{timeIST}</div>
          <div className="text-[10.5px] text-stone-500 font-tc-mono">{dateIST}</div>
        </div>
      </div>

      {/* FY + GST */}
      <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-stone-200/70">
        <div>
          <div className="text-[10px] font-tc-mono tracking-[0.24em] text-stone-500 flex items-center gap-1"><Clock size={9} /> FY</div>
          <div className="font-tc-mono text-[15px] font-bold text-stone-900">
            {fy.label} <span className="text-[#EA580C]">· {fy.quarter}</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] font-tc-mono tracking-[0.24em] text-stone-500 flex items-center gap-1"><Receipt size={9} /> STATUTORY</div>
          <div className={`font-tc-mono text-[12px] font-bold ${gstUrgencyTone(gst.days)}`}>
            {gst.label.toUpperCase()}
          </div>
          <div className="text-[10px] text-stone-500 font-tc-mono">for {gst.forMonth}</div>
        </div>
      </div>
    </div>
  );
}
